import React, { useCallback, useMemo } from "react";
import AutocompleteBase from "../autocomplete-base";
import { AutocompleteProps, TextFieldProps } from "@mui/material";

export interface ArTransactionTypesOption {
    id: string;
    title?: string;
}

interface ArTransactionTypesAutocompleteProps<M extends boolean = false> extends Omit<AutocompleteProps<ArTransactionTypesOption, M, false, false>, 'renderInput' | 'renderOption' | 'options'> {
    textFieldProps?: Partial<TextFieldProps>;
    form?: boolean;
    name?: string;
    required?: boolean;

    [key: string]: any;
}

const ArTransactionTypesAutocomplete = <M extends boolean = false,>({
    textFieldProps = {},
    form = false,
    name = undefined,
    required = false,
    ...props
}: ArTransactionTypesAutocompleteProps<M>) => {

    /**
     * Fetches AR transaction types options
     * @returns Array of AR transaction types options
     */
    const getOptions = useCallback(async () => {
        return [
            { id: 'C' },
            { id: 'D' },
            { id: 'I' },
            { id: 'P' },
            { id: 'S' }
        ] as ArTransactionTypesOption[];
    }, []);

    /**
     * Gets the title of an AR transaction type based on its ID
     * @param id - The ID of the AR transaction type
     */
    const getOptionTitle = useCallback((id: string) => {
        switch (id) {
            case 'C':
                return 'Credit memo';
            case 'D':
                return 'Debit memo';
            case 'I':
                return 'Invoice';
            case 'P':
                return 'Payment';
            case 'S':
                return 'Service Charge';
            default:
                return '';
        }
    }, []);

    /**
     * Checks if two AR transaction type options are equal based on their IDs
     * @param option - The first AR transaction type option
     * @param value - The second AR transaction type option
     */
    const isOptionEqualToValue = useCallback((option: ArTransactionTypesOption, value: ArTransactionTypesOption | null) =>
        option.id === value?.id, []);

    /**
     * Gets the display label for an AR transaction type option
     * @param option - The AR transaction type option
     */
    const getOptionLabel = useCallback((option: ArTransactionTypesOption) => getOptionTitle(option.id), [getOptionTitle]);

    /**
     * Gets the unique key for an AR transaction type option
     * @param option - The AR transaction type option
     */
    const getOptionKey = useCallback((option: ArTransactionTypesOption) => option?.id ?? "", []);

    const defaultOptions = useMemo(() => [], []);

    return (
        <AutocompleteBase<ArTransactionTypesOption, M>
            canSearchEmptyKeywords
            keywordsBased={false}
            getOptions={getOptions}
            textFieldProps={textFieldProps}
            form={form}
            name={name}
            required={required}
            isOptionEqualToValue={isOptionEqualToValue}
            getOptionLabel={getOptionLabel}
            getOptionKey={getOptionKey}
            options={defaultOptions}
            clearOnBlur={false}
            {...props}
        />
    );
};

export default ArTransactionTypesAutocomplete;
